import { computed } from 'vue'

export function useButtonClasses(props) {
  const staticClasses =
    'inline-flex items-center font-semibold transition-all duration-200 ease-in-out'

  const sizeClasses = computed(() => {
    if (props.shape == 'rounded') {
      if (props.size == 'xs') return 'px-2.5 py-1 text-xs'
      else if (props.size == 'sm') return 'px-2.5 py-1 text-sm'
      else if (props.size == 'md') return 'px-3 py-1.5 text-sm'
      else if (props.size == 'lg') return 'px-3.5 py-2 text-sm'
      else return 'px-4 py-2.5 text-sm'
    } else if (props.shape == 'circular') {
      if (props.size == 'xs') return 'p-1 text-xs'
      else if (props.size == 'sm') return 'p-1.5 text-sm'
      else if (props.size == 'md') return 'p-2 text-sm'
      else if (props.size == 'lg')
        return 'p-2 text-base' // doesn't have matching size in tailwindui
      else return 'p-3 text-base' // doesn't have matching size in tailwindui
    } else if (props.shape === 'default') {
      if (props.size == 'xs') return 'px-2 py-1 text-xs'
      else if (props.size == 'sm') return 'px-2 py-1 text-sm'
      else if (props.size == 'md') return 'px-2.5 py-1.5 text-sm'
      else if (props.size == 'lg') return 'px-3 py-2 text-sm'
      else return 'px-3.5 py-2.5 text-sm'
    } else {
      return props.shape
    }
  })

  const colorClasses = computed(() => {
    if (props.color == 'white')
      return [
        'shadow-sm text-gray-900 ring-1 ring-inset ring-gray-300 dark:text-white dark:ring-0',
        props.active
          ? 'bg-gray-700/10 dark:bg-gray-200/5 hover:bg-gray-900/10 dark:hover:bg-gray-500/10'
          : 'bg-white dark:bg-white/10 hover:bg-gray-50 dark:hover:bg-white/20',
      ].join(' ')
    else if (props.color == 'ghost')
      return [
        'text-gray-700 dark:text-gray-200',
        props.active
          ? 'bg-gray-900/10 dark:bg-white/10 hover:bg-gray-900/20 dark:hover:bg-white/20'
          : 'hover:bg-gray-900/5 dark:hover:bg-white/10',
      ].join(' ')
    else if (props.color == 'secondary')
      return 'shadow-sm bg-indigo-50 text-indigo-600 hover:bg-indigo-100 dark:text-indigo-200 dark:bg-indigo-800 dark:hover:bg-indigo-700'
    else if (props.color == 'red')
      return 'shadow-sm text-red-700 bg-red-100 hover:bg-red-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-600 dark:text-red-200 dark:bg-red-800 dark:hover:bg-red-700'
    else if (props.color == 'green')
      return 'shadow-sm text-green-700 bg-green-100 hover:bg-green-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-green-600 dark:text-green-200 dark:bg-green-900 dark:hover:bg-green-800'
    else if (props.color == 'error')
      return 'text-red-800 bg-red-50 hover:bg-red-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-600 dark:text-red-200 dark:bg-red-900 dark:hover:bg-red-800'
    else if (props.color == 'success')
      return 'text-green-800 bg-green-50 hover:bg-green-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-green-600 dark:text-green-200 dark:bg-green-900 dark:hover:bg-green-800'
    else if (props.color == 'warning')
      return 'text-yellow-800 bg-yellow-50 hover:bg-yellow-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-yellow-600 dark:text-yellow-200 dark:bg-yellow-900 dark:hover:bg-yellow-800'
    else if (props.color == 'information')
      return 'text-blue-800 bg-blue-50 hover:bg-blue-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 dark:text-blue-200 dark:bg-blue-900 dark:hover:bg-blue-800'
    else
      return 'shadow-sm bg-indigo-600 text-white hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 dark:bg-indigo-500 dark:text-white dark:hover:bg-indigo-400 dark:focus-visible:outline-indigo-500'
  })

  const roundedClasses = computed(() => {
    if (props.shape == 'default') {
      if (props.size == 'xs' || props.size == 'sm') {
        if (props.rounded == 'left') return 'focus:z-10 rounded-l'
        else if (props.rounded == 'right') return 'focus:z-10 rounded-r'
        else if (props.rounded == 'middle') return 'focus:z-10'
        else return 'rounded'
      } else {
        if (props.rounded == 'left') return 'focus:z-10 rounded-l-md'
        else if (props.rounded == 'right') return 'focus:z-10 rounded-r-md'
        else if (props.rounded == 'middle') return 'focus:z-10'
        else return 'rounded-md'
      }
    } else if (props.shape == 'rounded' || props.shape == 'circular') {
      return 'rounded-full'
    }
    return ''
  })

  const borderClasses = computed(() => {
    let borderClasses = ''
    if (props.rounded == 'right' || props.rounded == 'middle') {
      if (props.color == 'white') {
        // White buttons already have a ring border
        borderClasses += '-ml-px dark:border-l dark:border-gray-700 dark:ml-0'
      } else if (props.color == 'red' || props.color == 'error')
        borderClasses += 'border-l border-l-red-300 dark:border-l-red-400'
      else if (props.color == 'green' || props.color == 'success')
        borderClasses += 'border-l border-l-green-500 dark:border-l-green-600'
      else {
        // Only white buttons already have a ring border, so add a border to other buttons when they are in a group
        borderClasses += 'border-l border-l-indigo-300 dark:border-l-indigo-400'
      }
    }

    return borderClasses
  })

  const disabled = computed(() => props.disabled || props.processing)

  const disabledClasses = computed(() => (disabled.value ? 'opacity-50' : ''))

  return computed(() =>
    [
      staticClasses,
      sizeClasses.value,
      colorClasses.value,
      roundedClasses.value,
      borderClasses.value,
      disabledClasses.value,
    ].join(' ')
  )
}
